import React, { useCallback, useMemo, useState } from "react";
import {
    GRID_TREE_DATA_GROUPING_FIELD,
    GridColDef,
    GridComparatorFn,
    GridValueFormatterParams,
} from "@mui/x-data-grid-premium";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { DriverLogRow } from "../../../models/vehicle/driverLogRow";
import { AppUtils } from "../../../models/common/appUtils";
import { Base } from "../../../framework/base";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { ICostCenterItem } from "../../../models/costCenter/costCenterItem";
import { ICustomerItem } from "../../../models/customer/customerItem";
import {
    GroupingValue,
    setVehicleReportGrouping,
} from "../../../store/reportOptionsSlice";
import { useAppSelector } from "../../../framework/customStore";
import {
    GroupedDataGridRow,
    GroupedTreeDataGrid,
} from "../../framework/groupedDataGrid/groupedTreeDataGrid";
import { ReportGroupingPicker } from "../components/reportGroupingPicker";
import { ReportSection } from "../ReportSection";

interface DriverLogGridProps {
    rows: DriverLogRow[];
    vehicles: VehicleListItemDto[];
    employees: EmployeeListItemDto[];
    costCenters: ICostCenterItem[];
    customers: ICustomerItem[];
    filterByVehicles: (vehicleId: string) => boolean;
    filterByEmployees: (employeeId: string | null) => boolean;
    filterByCostCenters: (costCenterId: string | null) => boolean;
    filterByCustomers: (customerId: string | null) => boolean;
    noRowsText: string;
}

interface DriverLogGridRow extends GroupedDataGridRow {
    vehicleId: string;
    registerNumber: string;
    brand: string;
    employeeId: string | null;
    employeeFullName: string | null;
    customerId: string | null;
    costCenterId: string | null;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    fromAddress: string;
    toAddress: string;
    fromLat: number | null;
    fromLon: number | null;
    toLat: number | null;
    toLon: number | null;
    distance: number;
    canOdometerStart: number | null;
    canOdometerEnd: number | null;
    state: string;
    tripType: string;
}

export const DriverLogGrid = ({
    rows,
    vehicles,
    employees,
    costCenters,
    customers,
    filterByVehicles,
    filterByEmployees,
    filterByCostCenters,
    filterByCustomers,
    noRowsText,
}: DriverLogGridProps) => {
    const { t } = useTranslation();
    const [showStops, setShowStops] = useState(false);
    const selectedGrouping = useAppSelector(
        (state) => state.reportOptions.vehicleGrouping
    );

    const columns: GridColDef<DriverLogGridRow>[] = useMemo(
        () => [
            {
                field: "registerNumber",
                headerName: t("vehicle.vehicle"),
                minWidth: 150,
                flex: 1,
                groupable: false,
                valueGetter: (params) =>
                    params.row.registerNumber
                        ? params.row.brand
                            ? `${params.row.registerNumber} ${params.row.brand}`
                            : params.row.registerNumber
                        : "",
            },
            {
                field: "employeeFullName",
                headerName: t("employee.employee"),
                minWidth: 140,
                flex: 1,
                groupable: false,
                valueGetter: (params) => params.row.employeeFullName ?? "",
            },
            {
                field: "customerId",
                headerName: t("customer.customer"),
                minWidth: 140,
                flex: 1,
                groupable: false,
                valueGetter: (params) =>
                    customers?.find((c) => c.id === params.row.customerId)
                        ?.name ?? "",
            },
            {
                field: "costCenterId",
                headerName: t("costCenter.costCenter"),
                minWidth: 140,
                flex: 1,
                groupable: false,
                valueGetter: (params) =>
                    costCenters?.find((c) => c.id === params.row.costCenterId)
                        ?.name ?? "",
            },
            {
                field: "startTime",
                headerName: t("common.timeperiodStarts"),
                minWidth: 150,
                flex: 1,
                groupable: false,
                valueFormatter: (params: GridValueFormatterParams<string>) =>
                    params.value
                        ? Base.dayjsDateToDateTimeStr(params.value)
                        : "",
            },
            {
                field: "endTime",
                headerName: t("common.timeperiodEnds"),
                minWidth: 150,
                flex: 1,
                groupable: false,
                valueFormatter: (params: GridValueFormatterParams<string>) =>
                    params.value
                        ? Base.dayjsDateToDateTimeStr(params.value)
                        : "",
            },
            {
                field: "durationMinutes",
                headerName: t("common.duration"),
                minWidth: 120,
                groupable: false,
                type: "number",
                availableAggregationFunctions: ["sum"],
                valueFormatter: (params: GridValueFormatterParams<number>) =>
                    params.value != null && params.value > 0
                        ? AppUtils.getDurationStrByDurationMin(
                            params.value,
                            true
                        )
                        : "",
            },
            {
                field: "fromAddress",
                headerName: t("reporting.fromAddress"),
                minWidth: 200,
                flex: 2,
                groupable: false,
                valueGetter: (params) => {
                    if (params.row.fromAddress) return params.row.fromAddress;
                    if (
                        params.row.fromLat != null &&
                        params.row.fromLon != null
                    ) {
                        return `${params.row.fromLat.toFixed(5)}, ${params.row.fromLon.toFixed(5)}`;
                    }
                    return "";
                },
            },
            {
                field: "toAddress",
                headerName: t("reporting.toAddress"),
                minWidth: 200,
                flex: 2,
                groupable: false,
                valueGetter: (params) => {
                    if (params.row.toAddress) return params.row.toAddress;
                    if (params.row.toLat != null && params.row.toLon != null) {
                        return `${params.row.toLat.toFixed(5)}, ${params.row.toLon.toFixed(5)}`;
                    }
                    return "";
                },
            },
            {
                field: "distance",
                headerName: t("reporting.distance"),
                minWidth: 100,
                groupable: false,
                type: "number",
                availableAggregationFunctions: ["sum"],
                valueFormatter: (params: GridValueFormatterParams<number>) =>
                    params.value !== null &&
                    params.value !== undefined &&
                    params.value > 0
                        ? params.value.toLocaleFixed(2, "fi-FI")
                        : "",
            },
            {
                field: "canOdometerStart",
                headerName: t("reporting.canOdometerStart"),
                minWidth: 160,
                groupable: false,
                type: "number",
                availableAggregationFunctions: [],
                valueFormatter: (
                    params: GridValueFormatterParams<number | null>
                ) =>
                    params.value !== null && params.value !== undefined
                        ? params.value.toLocaleFixed(0, "fi-FI")
                        : "",
            },
            {
                field: "canOdometerEnd",
                headerName: t("reporting.canOdometerEnd"),
                minWidth: 160,
                groupable: false,
                type: "number",
                availableAggregationFunctions: [],
                valueFormatter: (
                    params: GridValueFormatterParams<number | null>
                ) =>
                    params.value !== null && params.value !== undefined
                        ? params.value.toLocaleFixed(0, "fi-FI")
                        : "",
            },
            {
                field: "state",
                headerName: t("reporting.segmentType"),
                minWidth: 110,
                groupable: false,
                valueFormatter: (
                    params: GridValueFormatterParams<string>
                ): string =>
                    params.value === "Stopped"
                        ? String(t("reporting.stopped"))
                        : params.value === "Moving"
                            ? String(t("reporting.moving"))
                            : "",
            },
            {
                field: "tripType",
                headerName: t("reporting.tripType"),
                minWidth: 110,
                groupable: false,
                valueFormatter: (
                    params: GridValueFormatterParams<string>
                ): string =>
                    params.value === "Work"
                        ? String(t("reporting.tripTypeWork"))
                        : params.value === "Unknown"
                            ? String(t("reporting.tripTypeUnknown"))
                            : "",
            },
        ],
        [t, customers, costCenters]
    );

    const getGroupingColLabel = useCallback(
        (id: string, depth: number) => {
            if (selectedGrouping[depth] === (GroupingValue.Vehicle as string)) {
                const vehicle = vehicles?.find((v) => v.id === id);
                return vehicle
                    ? vehicle.brand
                        ? `${vehicle.registerNumber} - ${vehicle.brand}`
                        : vehicle.registerNumber
                    : t("reporting.noFleet");
            }
            if (
                selectedGrouping[depth] === (GroupingValue.Employee as string)
            ) {
                return (
                    employees?.find((e) => e.id === id)?.fullName ??
                    t("reporting.noEmployee")
                );
            }
            if (
                selectedGrouping[depth] === (GroupingValue.Customer as string)
            ) {
                if (!id || !id.isGuid()) return t("reporting.noCustomer");
                return (
                    customers?.find((c) => c.id === id)?.name ??
                    t("reporting.customerNotFound", { id })
                );
            }
            if (
                selectedGrouping[depth] === (GroupingValue.CostCenter as string)
            ) {
                if (!id || !id.isGuid()) return t("reporting.noCostCenter");
                return (
                    costCenters?.find((c) => c.id === id)?.name ??
                    t("reporting.costCenterNotFound", { id })
                );
            }
            // Leaf row (depth at or beyond grouping levels): show blank
            if (
                selectedGrouping.length > 0 &&
                depth >= selectedGrouping.length
            ) {
                return "";
            }
            return id;
        },
        [selectedGrouping, vehicles, employees, customers, costCenters]
    );

    const filteredRows = useMemo(
        () =>
            rows
                .filter((r) => showStops || r.state !== "Stopped")
                .filter((r) => filterByVehicles(r.vehicleId))
                .filter((r) => filterByEmployees(r.employeeId))
                .filter((r) => filterByCostCenters(r.costCenterId))
                .filter((r) => filterByCustomers(r.customerId)),
        [
            rows,
            showStops,
            filterByVehicles,
            filterByEmployees,
            filterByCostCenters,
            filterByCustomers,
        ]
    );

    const gridRows: DriverLogGridRow[] = useMemo(() => {
        return filteredRows.map((r) => {
            const leafId = `${r.vehicleId}_${r.startTime}`;
            const groupingPath =
                selectedGrouping.length > 0
                    ? selectedGrouping.map(
                        (g) =>
                            ((r as Record<string, unknown>)[g] as string) ??
                              g + "notAvailable"
                    )
                    : [];
            const durationMinutes =
                r.startTime && r.endTime
                    ? Base.dayjsDiffInMinutes(r.startTime, r.endTime)
                    : 0;
            return {
                id: leafId,
                hierarchy: [...groupingPath, leafId],
                vehicleId: r.vehicleId,
                registerNumber: r.registerNumber,
                brand: r.brand,
                employeeId: r.employeeId,
                employeeFullName: r.employeeFullName,
                customerId: r.customerId,
                costCenterId: r.costCenterId,
                startTime: r.startTime,
                endTime: r.endTime,
                durationMinutes,
                fromAddress: r.fromAddress,
                toAddress: r.toAddress,
                fromLat: r.fromLat,
                fromLon: r.fromLon,
                toLat: r.toLat,
                toLon: r.toLon,
                distance: r.distance,
                canOdometerStart: r.canOdometerStart,
                canOdometerEnd: r.canOdometerEnd,
                state: r.state,
                tripType: r.tripType,
                totals: { distance: r.distance ?? 0, durationMinutes },
            };
        });
    }, [filteredRows, selectedGrouping]);

    const gridGroupColumnSortComparator = useCallback<GridComparatorFn<unknown>>((
        v1,
        v2,
        cellParams1,
        cellParams2
    ) => {
        const sortModel = cellParams1.api.state.sorting.sortModel as {
            field: string;
            sort: string;
        }[];
        const sortDirection =
            sortModel.find((m) => m.field === cellParams1.field)?.sort ?? "asc";

        const id1Parts = cellParams1.id.toString().split(/[_/]/);
        const id2Parts = cellParams2.id.toString().split(/[_/]/);
        const id1 = id1Parts[id1Parts.length - 1];
        const id2 = id2Parts[id2Parts.length - 1];
        const id1IsGuid = id1.isGuid();
        const id2IsGuid = id2.isGuid();

        if (id1IsGuid && !id2IsGuid) return sortDirection === "desc" ? 1 : -1;
        if (!id1IsGuid && id2IsGuid) return sortDirection === "desc" ? -1 : 1;

        if (typeof v1 === "string" && typeof v2 === "string") {
            return v1.localeCompare(v2);
        }
        return v1 < v2 ? -1 : 1;
    }, []);

    const groupingOptions = useMemo(
        () => [
            GroupingValue.Vehicle,
            GroupingValue.Employee,
            GroupingValue.Customer,
            GroupingValue.CostCenter,
        ],
        []
    );

    const additionalHiddenColumns = useMemo(() => {
        const hidden: string[] = [];
        if (selectedGrouping.includes(GroupingValue.Vehicle as string))
            hidden.push("registerNumber");
        if (selectedGrouping.includes(GroupingValue.Employee as string))
            hidden.push("employeeFullName");
        if (selectedGrouping.includes(GroupingValue.Customer as string))
            hidden.push("customerId");
        if (selectedGrouping.includes(GroupingValue.CostCenter as string))
            hidden.push("costCenterId");
        return hidden;
    }, [selectedGrouping]);

    return (
        <Grid2 container rowSpacing={2} pt={1}>
            <ReportSection
                id="driver-log-grouping"
                header={t("reporting.groupBy")}
                subsections={[
                    {
                        id: "grouping",
                        content: (
                            <ReportGroupingPicker
                                selectedGrouping={selectedGrouping}
                                options={groupingOptions}
                                setGrouping={setVehicleReportGrouping}
                                firstGroupingNoSelectionLabel={t(
                                    "reporting.selectGrouping"
                                )}
                            />
                        ),
                    },
                    {
                        id: "show-stops",
                        content: (
                            <FormControlLabel
                                id="driver-log-show-stops"
                                control={
                                    <Checkbox
                                        id="driver-log-show-stops-checkbox"
                                        checked={showStops}
                                        onChange={(e) =>
                                            setShowStops(e.target.checked)
                                        }
                                        size="small"
                                    />
                                }
                                label={t("reporting.showStops")}
                            />
                        ),
                    },
                ]}
            />

            <Grid2 xs={12}>
                <GroupedTreeDataGrid
                    noRowsText={noRowsText}
                    columns={columns}
                    rows={gridRows}
                    detailsComponent={null}
                    defaultSortModel={[
                        { field: GRID_TREE_DATA_GROUPING_FIELD, sort: "asc" },
                    ]}
                    groupingColDef={{
                        flex: 2,
                        minWidth: 220,
                        sortable: true,
                        sortingOrder: ["asc", "desc"],
                        headerName: t("reporting.grouping"),
                        sortComparator: gridGroupColumnSortComparator,
                        valueFormatter: (data) => {
                            const value = data?.value ?? t("reporting.total");
                            if ((value as string).isGuid()) {
                                return getGroupingColLabel(
                                    value as string,
                                    (data?.id as string).split("/").length - 2
                                );
                            }
                            return value as string;
                        },
                    }}
                    getGroupLabel={getGroupingColLabel}
                    persistStateKey="DriverLogGridState"
                    enableColumnSelector
                    showLeafLabelAsDate={false}
                    hideGroupingColumn={selectedGrouping.length === 0}
                    enableHideEmptyColumns={false}
                    additionalHiddenColumns={additionalHiddenColumns}
                    isGroupExpandedByDefault={
                        selectedGrouping.length > 0 ? () => true : undefined
                    }
                />
            </Grid2>
        </Grid2>
    );
};
